import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function CancellationPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Raleway', sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section className="bg-[#1C2B1F] pt-36 pb-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block text-amber-400 text-xs font-bold uppercase tracking-widest mb-3">Legal</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Cancellation Policy</h1>
          <p className="text-stone-300 text-base">Last updated: 15 April 2026</p>
        </div>
      </section>

      {/* Key summary bar */}
      <section className="bg-amber-50 border-y border-amber-200 py-6">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row gap-6 justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                <i className="ri-check-line text-green-600 text-lg"></i>
              </div>
              <div>
                <p className="font-bold text-zinc-900 text-sm">Before Deposit</p>
                <p className="text-zinc-600 text-xs">Cancel free of charge</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                <i className="ri-information-line text-amber-600 text-lg"></i>
              </div>
              <div>
                <p className="font-bold text-zinc-900 text-sm">After Survey (within 3 days)</p>
                <p className="text-zinc-600 text-xs">£350 cancellation fee</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0">
                <i className="ri-tools-line text-orange-600 text-lg"></i>
              </div>
              <div>
                <p className="font-bold text-zinc-900 text-sm">During Manufacture</p>
                <p className="text-zinc-600 text-xs">70% of Contract value</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-box-3-line text-red-600 text-lg"></i>
              </div>
              <div>
                <p className="font-bold text-zinc-900 text-sm">After Manufacture</p>
                <p className="text-zinc-600 text-xs">95% of Contract value</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-12">

          {/* Clause 15 - Cancellation */}
          <div>
            <h2 className="text-2xl font-bold text-zinc-900 mb-6 flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center bg-[#1C2B1F] text-amber-400 rounded-full text-sm font-bold flex-shrink-0">15</span>
              Cancellation
            </h2>

            <div className="bg-amber-50 border-l-4 border-amber-400 p-5 rounded-r-lg mb-8">
              <p className="text-zinc-700 text-sm leading-relaxed">
                The Customer may cancel the Contract without penalty prior to paying the deposit, which is when production will commence. Any cancellations must be given by written notice by email to{" "}
                <a href="mailto:sales@ensurewindows.co.uk" className="text-amber-600 hover:underline font-medium">
                  sales@ensurewindows.co.uk
                </a>
                . However, if the Customer decides to cancel the Contract after production is commenced, he/she shall be charged as follows:
              </p>
            </div>

            {/* Cancellation Fee Timeline */}
            <div className="space-y-4 mb-8">
              {/* Before deposit */}
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <div className="w-0.5 bg-stone-200 flex-1 mt-2"></div>
                </div>
                <div className="pb-8 flex-1">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                    <h3 className="font-bold text-green-800 text-base mb-1">Before Deposit Payment</h3>
                    <p className="text-green-700 text-sm leading-relaxed">
                      Cancellation is permitted <strong>free of charge</strong> at any point before the deposit is paid. Production only commences after deposit receipt.
                    </p>
                    <div className="mt-3 inline-flex items-center gap-1 bg-green-200 text-green-800 text-xs font-bold px-3 py-1 rounded-full">
                      <i className="ri-price-tag-3-line text-xs"></i>
                      No charge
                    </div>
                  </div>
                </div>
              </div>

              {/* After survey */}
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-full flex-shrink-0">
                    <i className="ri-clipboard-line text-white text-lg"></i>
                  </div>
                  <div className="w-0.5 bg-stone-200 flex-1 mt-2"></div>
                </div>
                <div className="pb-8 flex-1">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                    <h3 className="font-bold text-zinc-900 text-base mb-1">After Survey</h3>
                    <p className="text-zinc-700 text-sm leading-relaxed">
                      Within <strong>3 days</strong> of the survey being carried out, a cancellation fee of <strong>£350</strong> will be applied to all cancellations to cover the cost of the survey visit, measurements, and administrative processing.
                    </p>
                    <div className="mt-3 inline-flex items-center gap-1 bg-amber-200 text-amber-800 text-xs font-bold px-3 py-1 rounded-full">
                      <i className="ri-price-tag-3-line text-xs"></i>
                      £350 cancellation fee
                    </div>
                  </div>
                </div>
              </div>

              {/* During manufacture */}
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-full flex-shrink-0">
                    <i className="ri-tools-line text-white text-lg"></i>
                  </div>
                  <div className="w-0.5 bg-stone-200 flex-1 mt-2"></div>
                </div>
                <div className="pb-8 flex-1">
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
                    <h3 className="font-bold text-zinc-900 text-base mb-1">During Manufacture</h3>
                    <p className="text-zinc-700 text-sm leading-relaxed">
                      If cancellation is made while goods are in production, a charge of <strong>70% of the Contract value</strong> will be applied. This reflects the cost of bespoke materials, labour, and factory time already committed to your order.
                    </p>
                    <div className="mt-3 inline-flex items-center gap-1 bg-orange-200 text-orange-800 text-xs font-bold px-3 py-1 rounded-full">
                      <i className="ri-percent-line text-xs"></i>
                      70% of Contract value
                    </div>
                  </div>
                </div>
              </div>

              {/* After manufacture */}
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-red-500 rounded-full flex-shrink-0">
                    <i className="ri-box-3-line text-white text-lg"></i>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                    <h3 className="font-bold text-zinc-900 text-base mb-1">After Manufacture</h3>
                    <p className="text-zinc-700 text-sm leading-relaxed">
                      If cancellation is made after manufacture is complete, a charge of <strong>95% of the Contract value</strong> will be applied. At this stage the goods are bespoke and manufactured to order and cannot be resold.
                    </p>
                    <div className="mt-3 inline-flex items-center gap-1 bg-red-200 text-red-800 text-xs font-bold px-3 py-1 rounded-full">
                      <i className="ri-percent-line text-xs"></i>
                      95% of Contract value
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cancellation fee table */}
            <div className="overflow-x-auto rounded-xl border border-stone-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1C2B1F] text-white">
                    <th className="text-left px-5 py-3 font-semibold">Cancellation Stage</th>
                    <th className="text-left px-5 py-3 font-semibold">Fee</th>
                    <th className="text-left px-5 py-3 font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { stage: "Before deposit paid", fee: "£0", note: "No charge — full cancellation right", bg: "bg-green-50" },
                    { stage: "After survey (within 3 days)", fee: "£350", note: "Flat fee regardless of order size", bg: "bg-amber-50" },
                    { stage: "During manufacture", fee: "70% of Contract value", note: "Bespoke manufacturing costs already incurred", bg: "bg-orange-50" },
                    { stage: "After manufacture", fee: "95% of Contract value", note: "Goods made to order, cannot be resold", bg: "bg-red-50" },
                  ].map((row) => (
                    <tr key={row.stage} className={`${row.bg} border-t border-stone-200`}>
                      <td className="px-5 py-3 font-medium text-zinc-900">{row.stage}</td>
                      <td className="px-5 py-3 font-bold text-zinc-900">{row.fee}</td>
                      <td className="px-5 py-3 text-zinc-600">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-zinc-500 text-xs mt-4 italic">
              * All cancellations must be submitted in writing by email to sales@ensurewindows.co.uk. Verbal cancellations will not be accepted.
            </p>
          </div>

          {/* Clause 16 - Force Majeure */}
          <div>
            <h2 className="text-2xl font-bold text-zinc-900 mb-4 flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center bg-[#1C2B1F] text-amber-400 rounded-full text-sm font-bold flex-shrink-0">16</span>
              Force Majeure
            </h2>
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-6">
              <p className="text-zinc-600 text-sm leading-relaxed">
                Whilst the Company undertakes to do its utmost to execute every order within the specified period, the Company does not take responsibility for any default due to any act of God, war, strike, lockout, industrial action, fire, flood, drought, tempest or other event beyond the reasonable control of the Company.
              </p>
            </div>
          </div>

          {/* Clause 17 - General */}
          <div>
            <h2 className="text-2xl font-bold text-zinc-900 mb-4 flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center bg-[#1C2B1F] text-amber-400 rounded-full text-sm font-bold flex-shrink-0">17</span>
              General
            </h2>
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-6">
              <p className="text-zinc-600 text-sm leading-relaxed">
                If any provision of the Contract is found by any court, tribunal or administrative body of competent jurisdiction to be wholly or partly illegal, invalid, void, voidable, enforceable or unreasonable the remaining provisions of the Contract and the remainder of such provisions shall continue in full force and effect.
              </p>
            </div>
          </div>

          {/* Clause 18 - Contact */}
          <div>
            <h2 className="text-2xl font-bold text-zinc-900 mb-4 flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center bg-[#1C2B1F] text-amber-400 rounded-full text-sm font-bold flex-shrink-0">18</span>
              Contact
            </h2>
            <div className="bg-[#1C2B1F] text-white rounded-xl p-6">
              <p className="text-stone-300 text-sm mb-5">The Company contact details are as follows:</p>
              <address className="not-italic space-y-2">
                <p className="font-bold text-xl text-white">Ensure Windows</p>
                <p className="text-stone-300 text-sm">71-75 Shelton Street</p>
                <p className="text-stone-300 text-sm">London</p>
                <p className="text-stone-300 text-sm">WC2H 9JQ</p>
                <div className="pt-3 space-y-2">
                  <a href="mailto:sales@ensurewindows.co.uk" className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm cursor-pointer">
                    <div className="w-5 h-5 flex items-center justify-center"><i className="ri-mail-line"></i></div>
                    sales@ensurewindows.co.uk
                  </a>
                  <a href="tel:07852636300" className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm cursor-pointer">
                    <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-line"></i></div>
                    Tel: 07852 636 300
                  </a>
                </div>
              </address>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
